import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ECharts } from "echarts";
import type {
  AreaPopulationThresholds,
  TotalPopulationTrendUnconfiguredArea,
} from "@situate/contracts/runtime";
import { AnalyticsExportDialog } from "@/features/deployment-dashboard/analytics/analytics-export-dialog";
import { createAnalyticsExportTheme } from "@/features/deployment-dashboard/analytics/export/analytics-export.capture";
import type {
  ExportChartId,
  ExportFormat,
} from "@/features/deployment-dashboard/analytics/export/types";
import { useAnalyticsExport } from "@/features/deployment-dashboard/analytics/use-analytics-export";
import { DataCollectionNotice } from "@/features/deployment-dashboard/widgets/data-collection-notice";
import { AnalyticsFilterDashboard } from "@/features/deployment-dashboard/widgets/panels/analytics-filter-dashboard";
import { DensityTrendChart } from "@/features/deployment-dashboard/widgets/charts/density-trend";
import type { DensityTrendPoint } from "@/features/deployment-dashboard/widgets/charts/density-trend";
import {
  PopulationTrendAnalyticsChart,
  getPopulationTrendThresholds,
} from "@/features/deployment-dashboard/widgets/charts/population-trend-analytics";
import type { PopulationTrendAnalyticsPoint } from "@/features/deployment-dashboard/widgets/charts/population-trend-analytics";
import {
  getThroughputLineOptionKey,
  ThroughputTrendChart,
  type ThroughputLineTrendData,
  type ThroughputLineOption,
} from "@/features/deployment-dashboard/widgets/charts/throughput-trend";
import type { QueueWaitTrendData } from "@/features/deployment-dashboard/widgets/charts/queue-wait-trend";
import { QueueWaitTrendChart } from "@/features/deployment-dashboard/widgets/charts/queue-wait-trend";
import {
  ALL_AREAS_FILTER_VALUE,
  type AnalyticsFilters,
  type AreaLocation,
} from "@/features/deployment-dashboard/types";

interface AnalyticsChartState<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
  requestedAreaIds?: readonly string[];
  configuredAreaIds?: readonly string[];
  unconfiguredAreas?: readonly TotalPopulationTrendUnconfiguredArea[];
  thresholds?: AreaPopulationThresholds | null;
}

interface AnalyticsViewProps {
  deploymentId: string;
  filters: AnalyticsFilters;
  areas: AreaLocation[];
  onFiltersChange: (filters: AnalyticsFilters) => void;
  onReset: () => void;
  populationTrend: AnalyticsChartState<PopulationTrendAnalyticsPoint>;
  dutyFreePopulationTrend: AnalyticsChartState<PopulationTrendAnalyticsPoint>;
  densityTrend: AnalyticsChartState<DensityTrendPoint>;
  queueMetrics: { data: QueueWaitTrendData[]; isLoading: boolean; error: Error | null };
  throughputLineTrendData: ThroughputLineTrendData[];
  throughputLineTrendLoading: boolean;
  throughputLineTrendError: Error | null;
  throughputAllLineTrendData: ThroughputLineTrendData[];
  throughputLines: ThroughputLineOption[];
  throughputLinesLoading: boolean;
  throughputLinesError: Error | null;
  selectedThroughputLineKeys: ReadonlySet<string>;
  selectedThroughputLines: ThroughputLineOption[];
  onThroughputLinesChange: (keys: ReadonlySet<string>) => void;
}

export function AnalyticsView({
  deploymentId,
  filters,
  areas,
  onFiltersChange,
  onReset,
  populationTrend,
  dutyFreePopulationTrend,
  densityTrend,
  queueMetrics,
  throughputLineTrendData,
  throughputLineTrendLoading,
  throughputLineTrendError,
  throughputAllLineTrendData,
  throughputLines,
  throughputLinesLoading,
  throughputLinesError,
  selectedThroughputLineKeys,
  selectedThroughputLines,
  onThroughputLinesChange,
}: AnalyticsViewProps) {
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [hoveredThroughputLineKey, setHoveredThroughputLineKey] = useState<string | null>(null);
  const theme = useTheme();
  const chartInstances = useRef<Record<ExportChartId, ECharts | null>>({
    "population-trend": null,
    "density-trend": null,
    "throughput-trend": null,
  });
  const setChartInstance = useCallback(
    (chartId: ExportChartId) => (instance: ECharts) => {
      switch (chartId) {
        case "population-trend":
          chartInstances.current["population-trend"] = instance;
          break;
        case "density-trend":
          chartInstances.current["density-trend"] = instance;
          break;
        case "throughput-trend":
          chartInstances.current["throughput-trend"] = instance;
          break;
      }
    },
    []
  );

  const chartReadyHandlers = useMemo(
    () => ({
      population: setChartInstance("population-trend"),
      density: setChartInstance("density-trend"),
      throughput: setChartInstance("throughput-trend"),
    }),
    [setChartInstance]
  );

  const selectedAreaName =
    filters.areaId === ALL_AREAS_FILTER_VALUE
      ? "All Areas"
      : (areas.find((a) => a.id === filters.areaId)?.name ?? undefined);
  const hasDutyFreeMetric =
    filters.areaId !== ALL_AREAS_FILTER_VALUE &&
    areas.find((area) => area.id === filters.areaId)?.derivedPopulationMetrics
      ?.duty_free_population != null;

  const populationTrendThresholds = useMemo(
    () => getPopulationTrendThresholds(filters.areaId, populationTrend.thresholds),
    [filters.areaId, populationTrend.thresholds]
  );
  const chartXAxisRange = useMemo(
    () => ({ min: filters.startDate.getTime(), max: filters.endDate.getTime() }),
    [filters.startDate, filters.endDate]
  );

  const exportState = useAnalyticsExport({
    filters,
    areas,
    populationTrend,
    densityTrend,
    throughputLineTrendData,
    throughputLineTrendLoading,
    throughputLineTrendError,
    throughputLines,
    throughputLinesLoading,
    throughputLinesError,
    selectedThroughputLines,
    throughputAllLineTrendData,
    chartInstances: chartInstances.current,
    theme: createAnalyticsExportTheme(theme),
  });

  const { startExport } = exportState;
  const handleThroughputLinesChange = useCallback(
    (keys: ReadonlySet<string>) => {
      setHoveredThroughputLineKey((current) =>
        current != null &&
        throughputLines.some((line) => getThroughputLineOptionKey(line) === current)
          ? current
          : null
      );
      onThroughputLinesChange(keys);
    },
    [onThroughputLinesChange, throughputLines]
  );
  const handleThroughputLineHover = useCallback(
    (key: string | null) => {
      setHoveredThroughputLineKey(
        key != null && throughputLines.some((line) => getThroughputLineOptionKey(line) === key)
          ? key
          : null
      );
    },
    [throughputLines]
  );

  useEffect(() => {
    setHoveredThroughputLineKey((current) =>
      current != null &&
      throughputLines.some((line) => getThroughputLineOptionKey(line) === current)
        ? current
        : null
    );
  }, [throughputLines]);

  const handleExport = useCallback(
    async (format: ExportFormat, selectedCharts: readonly ExportChartId[]) => {
      if (await startExport(format, selectedCharts)) {
        setIsExportDialogOpen(false);
      }
    },
    [startExport]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <DataCollectionNotice />

      <AnalyticsFilterDashboard
        filters={filters}
        areas={areas}
        onFiltersChange={onFiltersChange}
        onReset={onReset}
        onExport={() => setIsExportDialogOpen(true)}
        isExporting={exportState.isExporting}
      />

      {/* Population and density charts — 2-column grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 2.5,
          alignItems: "stretch",
          minHeight: 400,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <PopulationTrendAnalyticsChart
            data={populationTrend.data}
            loading={populationTrend.loading}
            error={populationTrend.error}
            requestedAreaIds={populationTrend.requestedAreaIds}
            configuredAreaIds={populationTrend.configuredAreaIds}
            unconfiguredAreas={populationTrend.unconfiguredAreas}
            thresholds={populationTrendThresholds}
            xAxisRange={chartXAxisRange}
            onChartReady={chartReadyHandlers.population}
          />
          {hasDutyFreeMetric && (
            <PopulationTrendAnalyticsChart
              data={dutyFreePopulationTrend.data}
              loading={dutyFreePopulationTrend.loading}
              error={dutyFreePopulationTrend.error}
              xAxisRange={chartXAxisRange}
              metric="duty-free"
            />
          )}
        </Box>
        <DensityTrendChart
          data={densityTrend.data}
          xAxisRange={chartXAxisRange}
          loading={densityTrend.loading}
          error={densityTrend.error}
          onChartReady={chartReadyHandlers.density}
        />
      </Box>

      {/* Throughput chart — spans full available width */}
      <ThroughputTrendChart
        lineTrendData={throughputLineTrendData}
        loading={throughputLineTrendLoading}
        error={throughputLineTrendError}
        allLineTrendData={throughputAllLineTrendData}
        xAxisRange={chartXAxisRange}
        lines={throughputLines}
        linesLoading={throughputLinesLoading}
        linesError={throughputLinesError}
        selectedLineKeys={selectedThroughputLineKeys}
        onLineKeysChange={handleThroughputLinesChange}
        onLineHover={handleThroughputLineHover}
        deploymentId={deploymentId}
        areas={areas}
        hoveredLineKey={hoveredThroughputLineKey}
        selectedAreaName={selectedAreaName}
        onChartReady={chartReadyHandlers.throughput}
      />

      <QueueWaitTrendChart
        xAxisRange={chartXAxisRange}
        queues={queueMetrics.data}
        loading={queueMetrics.isLoading}
        error={queueMetrics.error}
      />

      <AnalyticsExportDialog
        open={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        filters={filters}
        areas={areas}
        selectedThroughputLines={selectedThroughputLines}
        isExporting={exportState.isExporting}
        errorMessage={exportState.errorMessage}
        onExport={handleExport}
        onClearError={exportState.clearError}
      />
    </Box>
  );
}
